"use client";

import React, { useState } from "react";
import { liteClient as algoliasearch } from "algoliasearch/lite";
import { InstantSearch } from "react-instantsearch";
import AlgoliaSearchResults from "./AlgoliaSearchResults";
import {
  isFreeSearchTerm,
  isAlgoliaPostIndex,
  getAlgoliaPostIndexName,
  ALGOLIA_AUTHORS_INDEX,
  ALGOLIA_BOOKS_INDEX,
} from "helpers/Helper";
import { useAppContext } from "@components/Provider";

const algoliaClient = algoliasearch(
  process.env.NEXT_PUBLIC_ALGOLIA_CLIENT_ID,
  process.env.NEXT_PUBLIC_ALGOLIA_API_KEY
);

const FREE_PRICE_FILTER = "promoprice = 0";
// Algolia can only return the first `paginationLimitedTo` hits of a query (default 1000, which is also the
// setting on our indices). Requests with offset + length beyond that return no hits.
const ALGOLIA_PAGINATION_LIMIT = 1000;

const FREE_TAG_FILTER = 'tagNames:"free"';

// Searches for "free" (and similar terms) list the free results first, followed by the results that match
// the typed text. optionalFilters can't do this because it only boosts records that already match the
// query, so these tabs run two queries per request and stitch them into a single paginated result:
//   1. priority results - empty query + a hard filter (books: promoprice = 0, articles: tag "free")
//   2. text matches     - the real query, excluding the priority results
// The genres tab (restrictSearchableAttributes) keeps its normal text search.
//
// To keep this cheap, everything is sent in as few Algolia calls as possible: identical requests are only
// run once, all priority queries (plus any regular requests) go out in one batch, and on page 0 the text query
// goes in the same batch because it starts at offset 0. Deeper pages need the priority count first, so their
// text queries follow in one second batch.
const getPaging = (params) => {
  const hitsPerPage = params.hitsPerPage || 20;
  const page = params.page || 0;
  return { hitsPerPage, page, offset: page * hitsPerPage };
};

// offset/length replace page/hitsPerPage, so remove them to avoid conflicting pagination params
const buildRangeRequest = (request, rangeParams, offset, length) => {
  const baseParams = { ...request.params };
  delete baseParams.page;
  delete baseParams.hitsPerPage;
  return { ...request, params: { ...baseParams, ...rangeParams, offset, length } };
};

const buildPriorityRequest = (job) => {
  const { hitsPerPage, offset } = getPaging(job.request.params);
  return buildRangeRequest(job.request, { query: "", filters: job.priorityFilter }, offset, hitsPerPage);
};

const buildTextRequest = (job, textOffset, length) => {
  const { params } = job.request;
  const filters = params.filters ? `(${params.filters}) AND NOT ${job.priorityFilter}` : `NOT ${job.priorityFilter}`;
  return buildRangeRequest(job.request, { filters }, textOffset, length);
};

const mergePriorityFirst = (job) => {
  const { hitsPerPage, page } = getPaging(job.request.params);
  const { priorityResult, textResult } = job;
  const priorityCount = priorityResult.nbHits;
  const remaining = hitsPerPage - priorityResult.hits.length;
  const totalHits = priorityCount + textResult.nbHits;
  // Only list pages Algolia can still return hits for. Each of the two queries is capped separately: the text
  // matches start after the priority results, and if the priority results alone exceed the limit, the text
  // matches are unreachable.
  const reachableHits =
    priorityCount >= ALGOLIA_PAGINATION_LIMIT
      ? ALGOLIA_PAGINATION_LIMIT
      : priorityCount + Math.min(textResult.nbHits, ALGOLIA_PAGINATION_LIMIT);
  return {
    ...textResult,
    hits: [...priorityResult.hits, ...(remaining > 0 ? textResult.hits.slice(0, remaining) : [])],
    nbHits: totalHits,
    nbPages: Math.ceil(reachableHits / hitsPerPage),
    page,
    hitsPerPage,
    offset: undefined,
    length: undefined,
  };
};

// Returns the filter that lists the free results first for this request, or null for a regular search.
const getPriorityFilter = (request) => {
  const { indexName, params = {} } = request;
  if (params.restrictSearchableAttributes || !isFreeSearchTerm(params.query)) {
    return null;
  }
  if (indexName === updateSearchIndex("books")) {
    return FREE_PRICE_FILTER;
  }
  // Article indices are per site, e.g. "1_POST"
  if (isAlgoliaPostIndex(indexName)) {
    return FREE_TAG_FILTER;
  }
  return null;
};

const searchClient = {
  ...algoliaClient,
  async search(requests) {
    const requestList = Array.isArray(requests) ? requests : requests.requests;

    // Identical priority requests (e.g. the root index and a child <Index> for the same tab) run once
    const jobs = new Map();
    const entries = requestList.map((request) => {
      const priorityFilter = getPriorityFilter(request);
      if (!priorityFilter) {
        return { request };
      }
      const key = JSON.stringify([request.indexName, request.params]);
      if (!jobs.has(key)) {
        jobs.set(key, { request, priorityFilter });
      }
      return { key };
    });

    // Batch 1: regular requests, every priority query, and the page-0 text queries
    const firstBatch = [];
    entries.forEach((entry) => {
      if (entry.request) {
        entry.index = firstBatch.push(entry.request) - 1;
      }
    });
    jobs.forEach((job) => {
      const { hitsPerPage, offset } = getPaging(job.request.params);
      job.priorityIndex = firstBatch.push(buildPriorityRequest(job)) - 1;
      if (offset === 0) {
        job.textIndex = firstBatch.push(buildTextRequest(job, 0, hitsPerPage)) - 1;
      }
    });
    const { results: firstResults } = await algoliaClient.search(firstBatch);

    // Batch 2: text queries for deeper pages, which start after the priority results
    const deeperJobs = [...jobs.values()].filter((job) => job.textIndex === undefined);
    const secondBatch = deeperJobs.map((job) => {
      const { hitsPerPage, offset } = getPaging(job.request.params);
      const priorityResult = firstResults[job.priorityIndex];
      const remaining = hitsPerPage - priorityResult.hits.length;
      return buildTextRequest(job, Math.max(0, offset - priorityResult.nbHits), Math.max(remaining, 1));
    });
    const secondResults = secondBatch.length ? (await algoliaClient.search(secondBatch)).results : [];
    deeperJobs.forEach((job, i) => {
      job.textResult = secondResults[i];
    });

    jobs.forEach((job) => {
      job.priorityResult = firstResults[job.priorityIndex];
      job.textResult = job.textResult || firstResults[job.textIndex];
      job.merged = mergePriorityFirst(job);
    });

    return { results: entries.map((entry) => (entry.request ? firstResults[entry.index] : jobs.get(entry.key).merged)) };
  },
};

const updateSearchIndex = (searchIndex, siteId = 1) => {
  if (searchIndex === "articles") {
    return getAlgoliaPostIndexName(siteId);
  } else if (searchIndex === "authors") {
    return ALGOLIA_AUTHORS_INDEX;
  } else {
    return ALGOLIA_BOOKS_INDEX;
  }
};

const SearchPage = (props) => {
  const { search, searchIndex: urlSearchIndex } = props;
  const { siteId } = useAppContext();
  const [searchText, setSearchText] = useState(search);
  const [searchIndex, setSearchIndex] = useState(updateSearchIndex(urlSearchIndex, siteId));

  const handleSearchIndex = (key, siteId = 1) => {
    setSearchIndex(updateSearchIndex(key, siteId));
  };

  const future = { preserveSharedStateOnUnmount: true };

  return (
    <InstantSearch
      searchClient={searchClient}
      indexName={searchIndex}
      stalledSearchDelay={300}
      future={future}
      initialUiState={{
        [searchIndex]: {
          query: searchText,
        },
      }}
    >
      <AlgoliaSearchResults
        searchText={searchText}
        setSearchText={setSearchText}
        searchIndex={searchIndex}
        handleSearchIndex={handleSearchIndex}
        urlSearchIndex={urlSearchIndex}
      />
    </InstantSearch>
  );
};

export default SearchPage;
